import { isValidHexColor } from "./validation";

type ColorPickerOptions = {
  onError?: (field: string, message?: string) => void;
};

const fallbackSwatchColor = "#d8dee6";

let activeColorInput: HTMLInputElement | null = null;
let activeColorButton: HTMLButtonElement | null = null;
let activeHue = 210;
let activeSaturation = 0.8;
let activeValue = 0.49;

function normalizeHexInput(value: string): string {
  const trimmed = value.trim();

  if (!trimmed) {
    return "";
  }

  return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
}

function hexToRgb(hex: string): { red: number; green: number; blue: number } {
  const normalized = normalizeHexInput(hex);
  const value = normalized.replace("#", "");

  return {
    red: Number.parseInt(value.slice(0, 2), 16),
    green: Number.parseInt(value.slice(2, 4), 16),
    blue: Number.parseInt(value.slice(4, 6), 16)
  };
}

function rgbToHex(red: number, green: number, blue: number): string {
  return `#${[red, green, blue]
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")}`;
}

function rgbToHsv(red: number, green: number, blue: number): {
  hue: number;
  saturation: number;
  value: number;
} {
  const redRatio = red / 255;
  const greenRatio = green / 255;
  const blueRatio = blue / 255;
  const max = Math.max(redRatio, greenRatio, blueRatio);
  const min = Math.min(redRatio, greenRatio, blueRatio);
  const delta = max - min;
  let hue = 0;

  if (delta !== 0) {
    if (max === redRatio) {
      hue = ((greenRatio - blueRatio) / delta) % 6;
    } else if (max === greenRatio) {
      hue = (blueRatio - redRatio) / delta + 2;
    } else {
      hue = (redRatio - greenRatio) / delta + 4;
    }
  }

  const degrees = Math.round(hue * 60);

  return {
    hue: degrees < 0 ? degrees + 360 : degrees,
    saturation: max === 0 ? 0 : delta / max,
    value: max
  };
}

function hsvToRgb(hue: number, saturation: number, value: number): {
  red: number;
  green: number;
  blue: number;
} {
  const chroma = value * saturation;
  const hueSegment = hue / 60;
  const x = chroma * (1 - Math.abs((hueSegment % 2) - 1));
  const match = value - chroma;
  let red = 0;
  let green = 0;
  let blue = 0;

  if (hueSegment >= 0 && hueSegment < 1) {
    red = chroma;
    green = x;
  } else if (hueSegment < 2) {
    red = x;
    green = chroma;
  } else if (hueSegment < 3) {
    green = chroma;
    blue = x;
  } else if (hueSegment < 4) {
    green = x;
    blue = chroma;
  } else if (hueSegment < 5) {
    red = x;
    blue = chroma;
  } else {
    red = chroma;
    blue = x;
  }

  return {
    red: (red + match) * 255,
    green: (green + match) * 255,
    blue: (blue + match) * 255
  };
}

export function initColorPicker(options: ColorPickerOptions = {}): void {
  const colorPickers = document.querySelectorAll<HTMLButtonElement>(".color-picker");
  const hexInputs = document.querySelectorAll<HTMLInputElement>(".hex-input");
  const colorPopover = document.querySelector<HTMLElement>("#color-popover");
  const colorPopoverTitle = document.querySelector<HTMLElement>("#color-popover-title");
  const colorPopoverClose =
    document.querySelector<HTMLButtonElement>(".color-popover-close");
  const colorPlane = document.querySelector<HTMLButtonElement>("#color-plane");
  const colorPlaneTarget = document.querySelector<HTMLElement>("#color-plane-target");
  const colorHueInput = document.querySelector<HTMLInputElement>("#color-hue");
  const colorPopoverHex = document.querySelector<HTMLInputElement>("#color-popover-hex");

  const setError = (message = ""): void => {
    options.onError?.("brand_colors", message);
  };

  const syncPalette = (): void => {
    hexInputs.forEach((input) => {
      const normalizedValue = normalizeHexInput(input.value);
      const swatchColor = isValidHexColor(normalizedValue)
        ? normalizedValue
        : fallbackSwatchColor;
      const swatch = document.querySelector<HTMLElement>(
        `[data-preview-swatch="${input.name}"]`
      );
      const picker = document.querySelector<HTMLButtonElement>(
        `[data-color-picker="${input.id}"]`
      );

      if (swatch) {
        swatch.style.backgroundColor = swatchColor;
      }

      if (picker) {
        picker.style.setProperty("--selected-color", swatchColor);
      }
    });
  };

  const updateCustomPickerUi = (): void => {
    if (!colorPlane || !colorPlaneTarget || !colorHueInput || !colorPopoverHex) {
      return;
    }

    const hueRgb = hsvToRgb(activeHue, 1, 1);
    const selectedRgb = hsvToRgb(activeHue, activeSaturation, activeValue);
    const hueColor = rgbToHex(hueRgb.red, hueRgb.green, hueRgb.blue);
    const selectedColor = rgbToHex(selectedRgb.red, selectedRgb.green, selectedRgb.blue);

    colorPlane.style.setProperty("--picker-hue", hueColor);
    colorPlaneTarget.style.left = `${activeSaturation * 100}%`;
    colorPlaneTarget.style.top = `${(1 - activeValue) * 100}%`;
    colorHueInput.value = String(activeHue);
    colorPopoverHex.value = selectedColor;
  };

  const setActiveColor = (hex: string): void => {
    const normalizedValue = normalizeHexInput(hex);

    if (!isValidHexColor(normalizedValue)) {
      return;
    }

    const { red, green, blue } = hexToRgb(normalizedValue);
    const hsv = rgbToHsv(red, green, blue);
    activeHue = hsv.hue;
    activeSaturation = hsv.saturation;
    activeValue = hsv.value;

    if (activeColorInput) {
      activeColorInput.value = normalizedValue.toLowerCase();
    }

    syncPalette();
    updateCustomPickerUi();
    setError();
  };

  const setActiveColorFromHsv = (): void => {
    const selectedRgb = hsvToRgb(activeHue, activeSaturation, activeValue);
    const color = rgbToHex(selectedRgb.red, selectedRgb.green, selectedRgb.blue);

    setActiveColor(color);
  };

  const closeColorPopover = (): void => {
    if (colorPopover) {
      colorPopover.hidden = true;
    }

    activeColorInput = null;
    activeColorButton = null;
  };

  const openColorPopover = (button: HTMLButtonElement): void => {
    const inputId = button.dataset.colorPicker;
    const input = inputId
      ? document.querySelector<HTMLInputElement>(`#${inputId}`)
      : null;

    if (!input || !colorPopover) {
      return;
    }

    activeColorInput = input;
    activeColorButton = button;

    if (colorPopoverTitle) {
      colorPopoverTitle.textContent = `Choose ${input.labels?.[0]?.textContent ?? "color"}`;
    }

    colorPopover.hidden = false;
    setActiveColor(input.value);
    colorPopoverHex?.focus();
  };

  const setColorFromPlaneEvent = (event: PointerEvent): void => {
    if (!colorPlane) {
      return;
    }

    const rect = colorPlane.getBoundingClientRect();
    const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
    const y = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);

    activeSaturation = rect.width === 0 ? 0 : x / rect.width;
    activeValue = rect.height === 0 ? 0 : 1 - y / rect.height;
    setActiveColorFromHsv();
  };

  hexInputs.forEach((input) => {
    input.addEventListener("input", (event) => {
      const hexInput = event.currentTarget as HTMLInputElement;
      const normalizedValue = normalizeHexInput(hexInput.value);

      if (isValidHexColor(normalizedValue)) {
        hexInput.value = normalizedValue.toLowerCase();
        syncPalette();

        if (activeColorInput === hexInput) {
          setActiveColor(normalizedValue);
        }

        setError();
      }

      syncPalette();
    });

    input.addEventListener("blur", (event) => {
      const hexInput = event.currentTarget as HTMLInputElement;
      const normalizedValue = normalizeHexInput(hexInput.value);

      if (isValidHexColor(normalizedValue)) {
        hexInput.value = normalizedValue.toLowerCase();
        syncPalette();
        setError();
        return;
      }

      setError("Enter colors as six-digit HEX values, such as #174a7c.");
      syncPalette();
    });
  });

  colorPickers.forEach((picker) => {
    picker.addEventListener("click", (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      openColorPopover(button);
    });
  });

  colorPopoverClose?.addEventListener("click", closeColorPopover);

  colorPopoverHex?.addEventListener("input", (event) => {
    const input = event.currentTarget as HTMLInputElement;
    const normalizedValue = normalizeHexInput(input.value);

    if (isValidHexColor(normalizedValue)) {
      setActiveColor(normalizedValue);
    }
  });

  colorPopoverHex?.addEventListener("blur", (event) => {
    const input = event.currentTarget as HTMLInputElement;
    const normalizedValue = normalizeHexInput(input.value);

    if (isValidHexColor(normalizedValue)) {
      setActiveColor(normalizedValue);
      return;
    }

    updateCustomPickerUi();
  });

  colorHueInput?.addEventListener("input", (event) => {
    activeHue = Number((event.currentTarget as HTMLInputElement).value);
    setActiveColorFromHsv();
  });

  colorPlane?.addEventListener("pointerdown", (event) => {
    setColorFromPlaneEvent(event);

    const handlePointerMove = (moveEvent: PointerEvent): void => {
      setColorFromPlaneEvent(moveEvent);
    };

    const handlePointerUp = (): void => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  });

  document.addEventListener("click", (event) => {
    if (
      !colorPopover ||
      colorPopover.hidden ||
      !event.target ||
      !(event.target instanceof Node)
    ) {
      return;
    }

    if (colorPopover.contains(event.target)) {
      return;
    }

    if (activeColorButton?.contains(event.target)) {
      return;
    }

    closeColorPopover();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      closeColorPopover();
    }
  });

  syncPalette();
}
